import { Controller } from '@hotwired/stimulus'

// Контроллер для dropdown меню
// - Открытие/закрытие по клику или hover
// - Закрытие по клику вне dropdown, по Escape
// - Поддержка вложенных dropdown
// - Интеграция с bottom sheet на мобильных
export default class DropdownController extends Controller {
  static targets = ['btn', 'content']

  connect() {
    this.isOpen = false
    this.leaveTimer = null

    this.boundEscape = this.handleEscape.bind(this)
    document.addEventListener('keydown', this.boundEscape)

    this.boundOutsideClick = this.handleOutsideClick.bind(this)
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundEscape)
    document.removeEventListener('click', this.boundOutsideClick)
  }

  toggle(event) {
    event?.preventDefault()
    event?.stopPropagation()

    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    if (this.isOpen) return

    // Закрываем другие открытые dropdown
    this.closeOthers()

    this.isOpen = true
    this.contentTarget.classList.add('is-open')
    this.btnTarget.classList.add('is-active')
    this.btnTarget.setAttribute('aria-expanded', 'true')

    this.alignContentToViewport()

    // Добавляем обработчик клика вне dropdown
    setTimeout(() => {
      document.addEventListener('click', this.boundOutsideClick)
    }, 0)

    // Dispatch события для интеграции с другими компонентами
    this.element.dispatchEvent(
      new CustomEvent('dropdown:open', {
        bubbles: true,
        detail: { controller: this }
      })
    )
  }

  close() {
    if (!this.isOpen) return

    this.isOpen = false
    this.contentTarget.classList.remove(
      'is-open',
      'is-align-end',
      'is-align-top'
    )
    this.btnTarget.classList.remove('is-active')
    this.btnTarget.setAttribute('aria-expanded', 'false')

    document.removeEventListener('click', this.boundOutsideClick)

    this.element.dispatchEvent(
      new CustomEvent('dropdown:close', {
        bubbles: true,
        detail: { controller: this }
      })
    )
  }

  // Закрытие других dropdown
  closeOthers() {
    const allDropdowns = document.querySelectorAll(
      '[data-controller~="dropdown"]'
    )
    allDropdowns.forEach((dropdown) => {
      if (dropdown === this.element) return

      // Не закрываем родительские dropdown (если этот dropdown внутри другого)
      if (dropdown.contains(this.element)) return

      // Закрываем дочерние dropdown (если они внутри этого)
      // и соседние dropdown (на том же уровне)
      const controller = this.application.getControllerForElementAndIdentifier(
        dropdown,
        'dropdown'
      )
      if (controller && controller.isOpen) {
        controller.close()
      }
    })
  }

  handleOutsideClick(event) {
    const target = event.target

    // Если клик внутри этого dropdown - игнорируем
    if (this.element.contains(target)) {
      return
    }

    this.close()
  }

  handleEscape(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close()
    }
  }

  // Если панель с left:0 вылезает за правый край вьюпорта — якорим к правому краю (left: auto; right: 0), чтобы не вылезать за правый край экрана
  alignContentToViewport() {
    const content = this.contentTarget
    const root = this.element

    content.classList.remove('is-align-end', 'is-align-top')

    if (root.classList.contains('dropdown__item')) return

    const run = () => {
      const rootRect = root.getBoundingClientRect()
      const listRect = content.getBoundingClientRect()
      const vw = document.documentElement.clientWidth
      const vh = window.innerHeight
      const pad = 8
      const w = listRect.width
      const h = listRect.height
      if (w <= 0) return

      const wouldOverflowRight = rootRect.left + w > vw - pad
      const fitsIfRightAnchored = rootRect.right - w >= pad
      const spaceBelow = vh - rootRect.bottom
      const spaceAbove = rootRect.top
      const shouldOpenTop = h > spaceBelow - pad && spaceAbove > spaceBelow

      if (wouldOverflowRight && fitsIfRightAnchored) {
        content.classList.add('is-align-end')
      }

      if (shouldOpenTop) {
        content.classList.add('is-align-top')
      }
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(run)
    })
  }

  itemClick(event) {
    const item = event.currentTarget
    this.#setActiveItem(item)
  }

  #clearActiveItems() {
    const items = this.element.querySelectorAll('.dropdown__item-btn.is-active')
    items.forEach((item) => {
      item.classList.remove('is-active')
    })
  }

  #setActiveItem(item) {
    this.#clearActiveItems()
    item.classList.add('is-active')
  }
}
